import {
  CheckCircleIcon,
  Controller,
  permissionsDescriptions,
  permissionsLabels,
  XCircleIcon,
} from "@";

const PermissionGroup = ({
  title,
  eyebrow,
  description,
  permissionKeys = [],
  control,
  watch,
  setValue,
  disabled = false,
  showGroupBadge = false,
  groupNameByKey,
}) => {
  const permissions = watch?.("permissions") || {};
  const total = permissionKeys.length;
  const checked = permissionKeys.filter((k) => permissions[k]).length;
  const allOn = total > 0 && checked === total;
  const noneOn = checked === 0;

  const handleSelectAll = () => {
    if (!setValue) return;
    permissionKeys.forEach((k) => setValue(`permissions.${k}`, true));
  };
  const handleClearAll = () => {
    if (!setValue) return;
    permissionKeys.forEach((k) => setValue(`permissions.${k}`, false));
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700/60">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-purple-900 dark:text-purple-400">
              {eyebrow}
            </p>
          )}
          <h2 className="truncate text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold tabular-nums ring-1 ${
              allOn
                ? "bg-green-500 text-white ring-green-700/20"
                : noneOn
                  ? "bg-gray-100 text-gray-500 ring-green-200 dark:bg-gray-700 dark:text-gray-400 dark:ring-green-600"
                  : "bg-green-50 text-green-500 ring-green-100 dark:bg-green-950/40 dark:text-green-300 dark:ring-green-800/40"
            }`}
          >
            {checked}
            <span className="opacity-60">/</span>
            {total}
            <span className="ms-1 font-medium opacity-80">مفعّلة</span>
          </span>

          {!disabled && total > 0 && (
            <>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={allOn}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800/40 dark:bg-gray-800 dark:text-emerald-400 dark:hover:bg-gray-700"
                aria-label="تحديد كل صلاحيات المجموعة"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                تحديد الكل
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={noneOn}
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-800/40 dark:bg-gray-800 dark:text-rose-400 dark:hover:bg-gray-700"
                aria-label="إلغاء كل صلاحيات المجموعة"
              >
                <XCircleIcon className="h-3.5 w-3.5" />
                مسح
              </button>
            </>
          )}
        </div>
      </header>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto p-2">
        <ul className="divide-y divide-gray-50 dark:divide-gray-700/40">
          {permissionKeys.map((key) => (
            <li key={key}>
              <Controller
                control={control}
                name={`permissions.${key}`}
                render={({ field }) => {
                  const value = !!field.value;
                  const toggle = () => !disabled && field.onChange(!value);
                  return (
                    <div
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      onClick={toggle}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          toggle();
                        }
                      }}
                      aria-pressed={value}
                      aria-disabled={disabled}
                      className={`group my-1 flex items-center justify-between gap-4 rounded-xl px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2  ${
                        disabled
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer"
                      } ${
                        value
                          ? "bg-purple-50/50 hover:bg-purple-50 dark:bg-purple-900/15 dark:hover:bg-purple-900/25"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className={` leading-snug ${
                            value
                              ? "font-semibold text-gray-900 dark:text-gray-100"
                              : "font-medium text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {permissionsLabels[key] || key}
                        </p>
                        {showGroupBadge ? (
                          groupNameByKey?.[key] && (
                            <>
                            <p className="mt-0.5 truncate text-[11px] font-medium text-purple-700/80 dark:text-purple-400/80">
                              {groupNameByKey[key]}

                            </p>
                              {
                                permissionsDescriptions?.[key] && (
                                  <p className="mt-1 text-sm leading-relaxed text-gray-400 max-w-3xl">
                                    {permissionsDescriptions[key]}
                                  </p>
                                )
                              }
                              </>
                          )
                        ) : (
                          permissionsDescriptions?.[key] && (
                            <p className="mt-1 text-sm leading-relaxed text-gray-400 max-w-3xl">
                              {permissionsDescriptions[key]}
                            </p>
                          )
                        )}
                      </div>

                      {/* Toggle (purely visual; click is on the row) */}
                      <span
                        aria-hidden
                        className={`relative inline-block h-[22px] w-11 shrink-0 rounded-full transition-colors duration-300 ${
                          value
                            ? "bg-gradient-to-l from-purple-700 to-purple-950 shadow-inner shadow-purple-900/20 dark:from-purple-500 dark:to-purple-700"
                            : "bg-gray-200 group-hover:bg-gray-300 dark:bg-gray-600 dark:group-hover:bg-gray-500"
                        }`}
                      >
                        <span
                          className={`absolute top-1/2 inline-block h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-white shadow-md shadow-gray-900/15 ring-1 transition-all duration-300 ease-out ${
                            value
                              ? "end-[3px] ring-purple-200 dark:ring-purple-800"
                              : "end-[23px] ring-gray-200 dark:ring-gray-500"
                          }`}
                        />
                      </span>
                    </div>
                  );
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PermissionGroup;
